import pbfe from "./pbfe.js";
import i18n, { _, t } from "./i18n.js";
import { LANG_URL_MAP, getLang, getCleanPath, navigate, switchLanguage } from "./index.js";

function createNavbar() {
    var navbar = new pbfe.Flexbox("row");
    navbar.element.id = "mainNavbar";
    navbar.alignItems = "center";
    navbar.justifyContent = "center";
    
    // Container for constrained width
    var container = new pbfe.Flexbox("row");
    container.element.className = "navbar-container";
    container.alignItems = "center";
    container.justifyContent = "space-between";
    container.element.style.width = "100%";

    // Helper for creating nav links
    var currentPath = getCleanPath();
    function createNavLink(key, targetPath) {
        var btn = new pbfe.Button("", "link");
        btn.element.className = "navbar-link";
        t(btn.element, key);
        
        var isCurrent = false;
        if (targetPath === "/") {
            if (currentPath === "/" || currentPath === "") isCurrent = true;
        } else {
            if (currentPath === targetPath) isCurrent = true;
        }
        
        if (isCurrent) {
            btn.element.classList.add("active");
        }
        
        btn.addEventListener("click", () => {
            var lang = getLang();
            var prefix = LANG_URL_MAP[lang];
            var path;
            if (targetPath === "/") {
                path = prefix ? "/" + prefix : "/";
            } else {
                path = prefix ? "/" + prefix + targetPath : targetPath;
            }
            
            navigate(path);
        });
        
        return btn;
    }

    // Left: Brand + Nav Links
    var leftGroup = new pbfe.Flexbox("row");
    leftGroup.alignItems = "center";
    leftGroup.gap = "2.5rem"; // Spacing between Brand and Menu

    // Brand
    var brandBox = new pbfe.Flexbox("row");
    brandBox.alignItems = "center";
    brandBox.gap = "0.75rem";
    brandBox.element.className = "navbar-brand";
    brandBox.element.style.cursor = "pointer";
    brandBox.element.addEventListener("click", () => {
        var lang = getLang();
        var prefix = LANG_URL_MAP[lang];
        var path = prefix ? "/" + prefix : "/";
        
        navigate(path); // Re-route
    });

    var icon = document.createElement("img");
    icon.src = new URL('./icons/icon.svg', import.meta.url);
    icon.className = "navbar-icon";
    icon.alt = "Logo";

    var title = document.createElement("h2");
    title.innerText = "Avatar Cropper";
    title.className = "navbar-title";

    brandBox.appendChild({ element: icon });
    brandBox.appendChild({ element: title });
    leftGroup.appendChild(brandBox);

    // Desktop Nav Links
    var menuBox = new pbfe.Flexbox("row");
    menuBox.alignItems = "center";
    menuBox.gap = "1.5rem";
    menuBox.element.className = "navbar-menu";

    menuBox.appendChild(createNavLink("Home", "/"));
    menuBox.appendChild(createNavLink("Blog", "/blog"));
    menuBox.appendChild(createNavLink("About", "/about"));
    menuBox.appendChild(createNavLink("Privacy", "/privacy"));
    menuBox.appendChild(createNavLink("Contact", "/contact"));
    menuBox.appendChild(createNavLink("Changelog", "/changelog"));

    leftGroup.appendChild(menuBox);
    container.appendChild(leftGroup);

    // Right: Language Selector + Hamburger
    var rightGroup = new pbfe.Flexbox("row");
    rightGroup.alignItems = "center";
    rightGroup.gap = "1rem"; // Add gap for spacing

    // Language Selector (Custom Dropdown)
    var langContainer = new pbfe.Widget();
    langContainer.element.className = "navbar-lang-container";
    
    // Trigger Button (Globe Icon)
    var langTrigger = new pbfe.Button("", "icon");
    langTrigger.element.className = "navbar-lang-trigger";
    t(langTrigger.element, "Select Language", "aria-label");
    langTrigger.element.innerHTML = `<svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>`;
    
    // Dropdown Menu
    var langDropdown = new pbfe.Flexbox("column");
    langDropdown.element.className = "navbar-lang-dropdown";
    
    var langs = i18n.getLanguages();
    var currentLang = localStorage.getItem("lang") || "en";
    
    for (const i in langs) {
        var item = new pbfe.Flexbox("row");
        item.element.className = "navbar-lang-item";
        item.alignItems = "center";
        item.justifyContent = "space-between";
        
        var text = langs[i].flag ? langs[i].flag + " " + langs[i].name : langs[i].name;
        var label = new pbfe.Label(text);
        item.appendChild(label);
        
        if (i === currentLang) {
            item.element.classList.add("selected");
            var check = document.createElement("div");
            check.className = "navbar-lang-check";
            check.innerHTML = `<svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>`;
            item.element.appendChild(check);
        }
        
        item.addEventListener("click", () => {
            if (i === currentLang) return;
            switchLanguage(i);
        });
        
        langDropdown.appendChild(item);
    }

    // Toggle
    langTrigger.addEventListener("click", (e) => {
        e.stopPropagation();
        langDropdown.element.classList.toggle("show");
    });

    // Close on outside click
    document.addEventListener("click", (e) => {
        if (!langContainer.element.contains(e.target)) {
            langDropdown.element.classList.remove("show");
        }
    });

    langContainer.appendChild(langTrigger);
    langContainer.appendChild(langDropdown);
    rightGroup.appendChild(langContainer);

    // Hamburger Button
    var hamburgerBtn = new pbfe.Button("", "icon");
    hamburgerBtn.element.className = "navbar-hamburger";
    t(hamburgerBtn.element, "Toggle Menu", "aria-label");
    // Animated hamburger lines
    hamburgerBtn.element.innerHTML = `<span></span><span></span><span></span>`;
    
    // Mobile Menu
    var mobileMenu = new pbfe.Flexbox("column");
    mobileMenu.element.className = "navbar-mobile-menu";
    mobileMenu.element.style.display = ""; // Remove inline flex style to allow CSS control
    
    // Mobile Links
    mobileMenu.appendChild(createNavLink("Home", "/"));
    mobileMenu.appendChild(createNavLink("Blog", "/blog"));
    mobileMenu.appendChild(createNavLink("About", "/about"));
    mobileMenu.appendChild(createNavLink("Privacy", "/privacy"));
    mobileMenu.appendChild(createNavLink("Contact", "/contact"));
    mobileMenu.appendChild(createNavLink("Changelog", "/changelog"));
    
    // Toggle Logic
    hamburgerBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        var isOpen = mobileMenu.element.classList.contains("open");
        if (isOpen) {
            mobileMenu.element.classList.remove("open");
            hamburgerBtn.element.classList.remove("active");
        } else {
            mobileMenu.element.classList.add("open");
            hamburgerBtn.element.classList.add("active");
        }
    });
    
    // Close when clicking outside
    document.addEventListener("click", (e) => {
        if (!mobileMenu.element.contains(e.target) && !hamburgerBtn.element.contains(e.target)) {
            mobileMenu.element.classList.remove("open");
            hamburgerBtn.element.classList.remove("active");
        }
    });

    rightGroup.appendChild(hamburgerBtn);
    container.appendChild(rightGroup);

    navbar.appendChild(container);
    navbar.appendChild(mobileMenu); // Append mobile menu to navbar (relative parent)

    return navbar;
}

export default createNavbar;
